import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { Typography } from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { MembershipDetails } from 'services/cart';
import { selectors as configSelectors, Configuration, MembershipSalesSettings } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { MembershipDetailsOverlayProps } from './MembershipDetailsOverlay.types';

const MembershipDetailsOverlay = (props: MembershipDetailsOverlayProps) => {
  const { membershipDetails, onConfirm } = props;
  const { t, i18n } = useTranslation();

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const membershipSalesSettingsList: MembershipSalesSettings[] = get(
    config,
    `${ticketingPath}.membershipSalesSettingsList`,
    []
  );

  const membershipSalesSettings = membershipSalesSettingsList.find(
    (mss) => mss.name === membershipDetails.membershipName
  );

  const getMembershipDetailsOverlayMessage = (messageType: string): string => {
    const { action, currentLevel, newLevel, newLevelStartDate, newLevelEndDate } =
      membershipDetails as MembershipDetails;
    const formattedStartDate = newLevelStartDate
      ? t('{{date, monthDayYear}}', { date: new Date(newLevelStartDate) })
      : '';
    const formattedEndDate = newLevelEndDate ? t('{{date, monthDayYear}}', { date: new Date(newLevelEndDate) }) : '';

    let message = '';

    if (action === 'renewal' && messageType === 'header') {
      message = get(
        membershipSalesSettings,
        `reservation.renewalOverlayMessages.header.${i18n.language}`,
        t('cart.renewalOverlayHeader')
      );
    } else if (action === 'renewal' && messageType === 'subheader') {
      message = get(
        membershipSalesSettings,
        `reservation.renewalOverlayMessages.subheader.${i18n.language}`,
        t('cart.renewalOverlaySubheader', {
          newMembershipLevel: newLevel,
          newMembershipStartDate: formattedStartDate
        })
      );
    } else if (action === 'upgrade' && messageType === 'header') {
      message = get(
        membershipSalesSettings,
        `reservation.upgradeOverlayMessages.header.${i18n.language}`,
        t('cart.upgradeOverlayHeader')
      );
    } else if (action === 'upgrade' && messageType === 'subheader') {
      message = get(
        membershipSalesSettings,
        `reservation.upgradeOverlayMessages.subheader.${i18n.language}`,
        t('cart.upgradeOverlaySubheader', {
          currentMembershipLevel: currentLevel,
          newMembershipLevel: newLevel,
          newMembershipEndDate: formattedEndDate
        })
      );
    }

    // Replace macros that could be in the configured messaging
    message = message
      .replace('{{currentMembershipLevel}}', currentLevel)
      .replace('{{newMembershipLevel}}', newLevel)
      .replace('{{newMembershipStartDate}}', formattedStartDate)
      .replace('{{newMembershipEndDate}}', formattedEndDate);

    return message;
  };

  return (
    <ActionOverlay
      title={getMembershipDetailsOverlayMessage('header')}
      description={
        <Typography align="center" fontSize={30}>
          {getMembershipDetailsOverlayMessage('subheader')}
        </Typography>
      }
      confirmText={t('commonText.close')}
      onConfirm={onConfirm}
      height={500}
      style={{ background: 'rgba(255,255,255,0.95)' }}
    />
  );
};

export default MembershipDetailsOverlay;
